import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import MaterialReactTable from "material-react-table";
import { MRT_Localization_ES } from "material-react-table/locales/es";
import moment from "moment";
import Navbar from "../../Navbar";
import { formatCuit } from "../pago_proveedores/utils";
import { getMenuesUsuario } from "../cheques_proveedores/ApiServices";
import {
  BANDEJAS,
  MENU_CHEQUES,
  normalizarRutaMenu,
  rutaBandeja,
} from "../cheques_proveedores/cheques.constants";
import {
  extraerEmisoresListaNegra,
  getListaNegra,
  getListaNegraByCuit,
} from "./ApiServices";
import ModalLiberar from "./ModalLiberar";
import {
  MENU_LISTA_NEGRA,
  TAB_ACTIVOS,
  TAB_HISTORICOS,
  labelMotivoRechazo,
  labelTipoEvento,
  rutaListaNegra,
} from "./listaNegra.constants";
import {
  codigoMotivoRechazo,
  textoMotivoRechazo,
} from "../cheques_proveedores/chequesRechazos.constants";

const PAGE_SIZE = 50;

const formatearFecha = (value) => {
  if (!value) return "—";
  const m = moment(value);
  return m.isValid() ? m.format("DD/MM/YYYY") : "—";
};

const formatearFechaHora = (value) => {
  if (!value) return "—";
  const m = moment(value);
  return m.isValid() ? m.format("DD/MM/YYYY HH:mm") : "—";
};

const filtrosIniciales = () => ({ q: "" });

const CampoDetalle = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2">{value ?? "—"}</Typography>
  </Grid>
);

const motivosDe = (row) => {
  const texto = textoMotivoRechazo(row);
  if (!texto) return "—";
  return texto
    .split(", ")
    .map((m) => labelMotivoRechazo(m))
    .join(", ");
};

const observacionesDe = (row) =>
  row?.obs_alta ??
  row?.obs ??
  row?.ultimo_rechazo?.obs ??
  null;

const tipoEventoDe = (ev) =>
  ev?.accion ?? ev?.tipo ?? ev?.evento ?? null;

const usuarioEventoDe = (ev) =>
  ev?.user ?? ev?.usuario ?? ev?.user_name ?? null;

const motivoEventoDe = (ev) =>
  ev?.motivo ??
  ev?.obs ??
  ev?.payload?.motivo ??
  null;

const esActivo = (row) =>
  row?.activo === true || row?.activo === 1 || row?.activo === "1";

export default function ListadoListaNegra() {
  const [tab, setTab] = useState(TAB_ACTIVOS);
  const [filtros, setFiltros] = useState(filtrosIniciales);
  const [filtrosAplicados, setFiltrosAplicados] = useState(filtrosIniciales);
  const [page, setPage] = useState(0);
  const [emisores, setEmisores] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [eventos, setEventos] = useState([]);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [errorDetalle, setErrorDetalle] = useState("");
  const [modalLiberar, setModalLiberar] = useState({ open: false, emisor: null });
  const [menusCheques, setMenusCheques] = useState({
    cc: false,
    finanzas: false,
    cartera: false,
  });
  const [toast, setToast] = useState({ open: false, message: "", severity: "info" });

  useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        const lista = await getMenuesUsuario();
        if (cancelado) return;
        const rutas = new Set(
          (Array.isArray(lista) ? lista : []).map((m) =>
            normalizarRutaMenu(m?.ruta)
          )
        );
        setMenusCheques({
          cc: rutas.has(MENU_CHEQUES.CC),
          finanzas: rutas.has(MENU_CHEQUES.FINANZAS),
          cartera: rutas.has(MENU_CHEQUES.CARTERA),
        });
      } catch {
        // opcional
      }
    })();
    return () => {
      cancelado = true;
    };
  }, []);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getListaNegra({
        activo: tab === TAB_ACTIVOS ? 1 : 0,
        q: filtrosAplicados.q,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      const lista = extraerEmisoresListaNegra(data);
      setEmisores(lista);
      setTotal(Number(data?.total ?? lista.length ?? 0));
    } catch (err) {
      setError(err.message || "Error al cargar emisores bloqueados");
      setEmisores([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [tab, filtrosAplicados.q, page]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const abrirDetalle = async (row) => {
    const cuit = row?.cuit ?? row?.cuit_librador;
    if (!cuit) return;
    setDetalle(row);
    setEventos([]);
    setErrorDetalle("");
    setLoadingDetalle(true);
    try {
      const data = await getListaNegraByCuit(cuit);
      const emisor =
        data?.item ?? data?.emisor ?? data?.lista_negra ?? row;
      setDetalle(emisor);
      setEventos(
        Array.isArray(data?.eventos)
          ? data.eventos
          : Array.isArray(emisor?.eventos)
            ? emisor.eventos
            : []
      );
    } catch (err) {
      setErrorDetalle(err.message || "No se pudo cargar el detalle");
    } finally {
      setLoadingDetalle(false);
    }
  };

  const aplicarFiltros = (e) => {
    e?.preventDefault?.();
    setFiltrosAplicados({ ...filtros });
    setPage(0);
  };

  const limpiarFiltros = () => {
    const limpios = filtrosIniciales();
    setFiltros(limpios);
    setFiltrosAplicados(limpios);
    setPage(0);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "cuit",
        header: "CUIT",
        size: 140,
        Cell: ({ row }) => (
          <Typography variant="body2" noWrap>
            {formatCuit(row.original?.cuit ?? row.original?.cuit_librador)}
          </Typography>
        ),
      },
      {
        accessorKey: "razon_social",
        header: "Razón social librador",
        size: 220,
        Cell: ({ row }) => {
          const nombre =
            row.original?.razon_social ??
            row.original?.razon_social_librador ??
            "—";
          return (
            <Typography
              variant="body2"
              noWrap
              title={nombre}
              sx={{ maxWidth: 220 }}
            >
              {nombre}
            </Typography>
          );
        },
      },
      {
        accessorKey: "fecha_bloqueo",
        header: "Fecha bloqueo",
        size: 120,
        Cell: ({ row }) =>
          formatearFecha(
            row.original?.fecha_bloqueo ??
              row.original?.fecha_alta ??
              row.original?.created_at
          ),
      },
      {
        accessorKey: "nro_cheque",
        header: "Cheque origen",
        size: 120,
        Cell: ({ row }) =>
          row.original?.nro_cheque ??
          row.original?.cheque?.nro_cheque ??
          row.original?.id_cheque ??
          "—",
      },
      {
        id: "motivos",
        header: "Motivos rechazo",
        size: 200,
        Cell: ({ row }) => {
          const motivos = motivosDe(row.original);
          return (
            <Typography
              variant="body2"
              noWrap
              title={motivos}
              sx={{ maxWidth: 200 }}
            >
              {motivos}
            </Typography>
          );
        },
      },
      {
        accessorKey: "obs",
        header: "Observaciones",
        size: 180,
        Cell: ({ row }) => {
          const obs = observacionesDe(row.original) ?? "—";
          return (
            <Typography
              variant="body2"
              noWrap
              title={obs}
              sx={{ maxWidth: 180 }}
            >
              {obs}
            </Typography>
          );
        },
      },
      {
        accessorKey: "cantidad_rechazos",
        header: "Rechazos hist.",
        size: 100,
        Cell: ({ row }) =>
          row.original?.cantidad_rechazos ??
          row.original?.total_rechazos ??
          row.original?.rechazos_count ??
          "—",
      },
      {
        id: "acciones",
        header: "Acciones",
        size: 220,
        enableSorting: false,
        Cell: ({ row }) => {
          const emisor = row.original;
          const activo = esActivo(emisor) || tab === TAB_ACTIVOS;
          const puedeLiberar =
            emisor?.puede_liberar !== false && activo;
          return (
            <Stack
              direction="row"
              spacing={0.5}
              flexWrap="wrap"
              useFlexGap
              onClick={(e) => e.stopPropagation()}
            >
              <Button
                size="small"
                variant="outlined"
                onClick={() => abrirDetalle(emisor)}
              >
                Detalle
              </Button>
              {puedeLiberar ? (
                <Button
                  size="small"
                  color="success"
                  variant="outlined"
                  onClick={() => setModalLiberar({ open: true, emisor })}
                >
                  Rehabilitar
                </Button>
              ) : null}
            </Stack>
          );
        },
      },
    ],
    [tab]
  );

  const tieneMenusCheques =
    menusCheques.cc || menusCheques.finanzas || menusCheques.cartera;

  return (
    <>
      <Navbar />
      <Container maxWidth="xl" sx={{ pt: "20px", pb: 4, px: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Typography variant="h5" fontWeight={700}>
            Emisores bloqueados (lista negra)
          </Typography>
          {tieneMenusCheques ? (
            <Button
              component={RouterLink}
              to={
                menusCheques.finanzas
                  ? rutaBandeja(BANDEJAS.FINANZAS)
                  : menusCheques.cc
                    ? rutaBandeja(BANDEJAS.CC)
                    : rutaBandeja(BANDEJAS.CARTERA)
              }
              variant="outlined"
              size="small"
            >
              Cheques
            </Button>
          ) : null}
        </Stack>

        <Alert severity="info" sx={{ mb: 2 }}>
          CUITs emisores/libradores bloqueados por rechazo bancario. La salida
          es manual desde esta pantalla. Al registrar un rechazo, el CUIT entra
          automáticamente.
        </Alert>

        <Tabs
          value={tab}
          onChange={(_, v) => {
            setTab(v);
            setPage(0);
          }}
          sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="Activos" value={TAB_ACTIVOS} />
          <Tab label="Históricos (liberados)" value={TAB_HISTORICOS} />
        </Tabs>

        <Paper
          component="form"
          onSubmit={aplicarFiltros}
          elevation={1}
          sx={{ p: 2, mb: 2, backgroundColor: "#fff" }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <FilterListIcon color="action" fontSize="small" />
            <Typography variant="subtitle2" fontWeight={600}>
              Filtros
            </Typography>
          </Stack>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Buscar"
                placeholder="CUIT o razón social"
                value={filtros.q}
                onChange={(e) =>
                  setFiltros((p) => ({ ...p, q: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Stack direction="row" spacing={1}>
                <Button type="submit" variant="contained" size="small">
                  Buscar
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={limpiarFiltros}
                >
                  Limpiar
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <MaterialReactTable
              columns={columns}
              data={emisores}
              getRowId={(row) =>
                String(row?.cuit ?? row?.cuit_librador ?? row?.id ?? "")
              }
              localization={MRT_Localization_ES}
              enableColumnActions={false}
              enableColumnFilters={false}
              enableDensityToggle={false}
              enableFullScreenToggle={false}
              enableHiding={false}
              enablePagination={false}
              initialState={{ density: "compact" }}
              muiTableBodyRowProps={({ row }) => ({
                onClick: (e) => {
                  if (e.target?.closest?.("button, input, .MuiCheckbox-root")) {
                    return;
                  }
                  abrirDetalle(row.original);
                },
                sx: {
                  cursor: "pointer",
                  backgroundColor: row.index % 2 === 0 ? "#fff" : "#f7f7f7",
                },
              })}
              muiTableHeadCellProps={{
                sx: {
                  fontSize: 14,
                  color: "white",
                  fontWeight: "normal",
                  backgroundColor: "#2471A3",
                  borderColor: "black",
                },
              }}
              muiTableBodyCellProps={{
                sx: { borderColor: "black" },
              }}
              muiTablePaperProps={{
                elevation: 0,
                sx: {
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                },
              }}
              renderTopToolbarCustomActions={() => (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {total > 0
                      ? `${total} emisor${total === 1 ? "" : "es"}`
                      : "Sin resultados"}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={cargar}
                  >
                    Actualizar
                  </Button>
                </Stack>
              )}
              renderEmptyRowsFallback={() => (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ p: 3, textAlign: "center" }}
                >
                  {tab === TAB_ACTIVOS
                    ? "No hay CUITs activos en lista negra."
                    : "No hay CUITs liberados en el historial."}
                </Typography>
              )}
            />

            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
              }
            />
          </>
        )}
      </Container>

      <Dialog
        open={Boolean(detalle)}
        onClose={() => setDetalle(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Detalle — CUIT {formatCuit(detalle?.cuit ?? detalle?.cuit_librador)}
        </DialogTitle>
        <DialogContent dividers>
          {loadingDetalle ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              {errorDetalle ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errorDetalle}
                </Alert>
              ) : null}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <CampoDetalle
                  label="Razón social"
                  value={
                    detalle?.razon_social ?? detalle?.razon_social_librador
                  }
                />
                <CampoDetalle
                  label="Estado"
                  value={esActivo(detalle) ? "Bloqueado" : "Liberado"}
                />
                <CampoDetalle
                  label="Fecha bloqueo"
                  value={formatearFecha(
                    detalle?.fecha_bloqueo ??
                      detalle?.fecha_alta ??
                      detalle?.created_at
                  )}
                />
                <CampoDetalle
                  label="Rechazos históricos"
                  value={
                    detalle?.cantidad_rechazos ??
                    detalle?.total_rechazos ??
                    detalle?.rechazos_count
                  }
                />
                <CampoDetalle
                  label="Motivos último rechazo"
                  value={motivosDe(detalle)}
                />
                {codigoMotivoRechazo(detalle) != null ? (
                  <CampoDetalle
                    label="Código motivo"
                    value={codigoMotivoRechazo(detalle)}
                  />
                ) : null}
                <CampoDetalle
                  label="Observaciones"
                  value={observacionesDe(detalle)}
                />
                <CampoDetalle
                  label="Usuario alta"
                  value={detalle?.user_alta}
                />
                {detalle?.fecha_liberacion ? (
                  <CampoDetalle
                    label="Fecha liberación"
                    value={formatearFecha(detalle.fecha_liberacion)}
                  />
                ) : null}
              </Grid>
              <Typography variant="subtitle2" gutterBottom>
                Historial de altas y liberaciones
              </Typography>
              {eventos.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Sin eventos registrados.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {eventos.map((ev, idx) => {
                    const tipo = tipoEventoDe(ev);
                    const usuario = usuarioEventoDe(ev);
                    const motivoEv = motivoEventoDe(ev);
                    return (
                      <Paper
                        key={ev?.id ?? idx}
                        variant="outlined"
                        sx={{ p: 1.5 }}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          flexWrap="wrap"
                        >
                          <Chip
                            size="small"
                            label={labelTipoEvento(tipo)}
                            color={
                              tipo === "liberacion" ? "success" : "error"
                            }
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            {formatearFechaHora(ev?.fecha ?? ev?.created_at)}
                          </Typography>
                          {usuario ? (
                            <Typography variant="caption">
                              — {usuario}
                            </Typography>
                          ) : null}
                        </Stack>
                        {motivoEv ? (
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {motivoEv}
                          </Typography>
                        ) : null}
                      </Paper>
                    );
                  })}
                </Stack>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {esActivo(detalle) && detalle?.puede_liberar !== false ? (
            <Button
              color="success"
              variant="contained"
              onClick={() =>
                setModalLiberar({ open: true, emisor: detalle })
              }
            >
              Rehabilitar
            </Button>
          ) : null}
          <Button onClick={() => setDetalle(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <ModalLiberar
        open={modalLiberar.open}
        emisor={modalLiberar.emisor}
        onClose={() => setModalLiberar({ open: false, emisor: null })}
        onOk={() => {
          setModalLiberar({ open: false, emisor: null });
          setDetalle(null);
          setToast({
            open: true,
            message: "CUIT rehabilitado correctamente",
            severity: "success",
          });
          cargar();
        }}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((t) => ({ ...t, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export { MENU_LISTA_NEGRA, rutaListaNegra };
